import React, { useEffect, useMemo, useState } from 'react'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import Box from '@mui/material/Box'
import TableBody from './TableBody'
import TableHeadColumns from './TableHeadColumns'
import Settings from './Setting'
import { useDispatch } from 'react-redux'
import {
  updateColumnTable,
  updateColumnTableCurriculum,
  updateRowsPerPage,
  updateSkip
} from '../../../store/actions/datatable.actions'

function descendingComparator(a, b, orderBy, columns = []) {
  const column = columns?.find((col) => col.key === orderBy)
  // Find the column definition to check if it has a sortKey
  const sortField = column?.sortKey || orderBy

  if (column?.sortKey) {
    let aValue = a?.[sortField]
    let bValue = b?.[sortField]

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0
    if (aValue == null) return 1
    if (bValue == null) return -1

    // Convert to strings and use localeCompare with numeric option
    return String(bValue).localeCompare(String(aValue), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  } else {
    if (b?.[orderBy] < a?.[orderBy]) {
      return -1
    }
    if (b?.[orderBy] > a?.[orderBy]) {
      return 1
    }
    return 0
  }
}

function getComparator(order, orderBy, columns = []) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, columns)
    : (a, b) => -descendingComparator(a, b, orderBy, columns)
}

const DataGrid = ({
  id = 'data-grid',
  loading,
  rows = [],
  columns = [],
  totalRows,
  rowsPerPage = 5,
  skip = 0,
  labelNoRows = "You don't have any rows yet",
  showSetting = true,
  actionsSetting = null,
  nameModule,
  groupBy,
  orderGroupBy = 'asc',
  groupByRender,
  orderByField = 'code',
  orderOrder = 'asc',
  curriculumId,
  ...props
}) => {
  const [orderBy, setOrderBy] = useState(orderByField)
  const [order, setOrder] = useState(orderOrder)
  const [page, setPage] = useState(0)
  const [rowsPP, setRowsPerPage] = useState(rowsPerPage)
  const [newColumns, setNewColumns] = useState(columns)
  const dispatch = useDispatch()

  const handleRequestSort = (event, property) => {
    event.preventDefault()
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleChangePage = (event, newPage) => {
    event.preventDefault()
    if (nameModule) {
      dispatch(updateSkip(nameModule, newPage))
    }
    setPage(newPage)
  }
  const handleChangeRowsPerPage = (event) => {
    event.preventDefault()
    const value = parseInt(event.target.value, 10)
    if (nameModule) {
      dispatch(updateRowsPerPage(nameModule, value))
    }
    setRowsPerPage(value)
    handleChangePage(event, 0)
  }
  const handleShowColumns = (columns) => {
    if (nameModule) {
      if (curriculumId) {
        dispatch(updateColumnTableCurriculum(curriculumId, nameModule, columns))
      } else {
        dispatch(updateColumnTable(nameModule, columns))
      }
    }
    setNewColumns(columns)
  }
  useEffect(() => {
    setRowsPerPage(rowsPerPage)
  }, [rowsPerPage])
  useEffect(() => {
    setPage(skip)
  }, [skip])
  useEffect(() => {
    // Preserve user-toggled show/hide state when the columns definition changes
    // (e.g. after autosave triggers a re-render with a new columns reference).
    // Only add genuinely new columns with their default show value.
    setNewColumns((prev) => {
      if (!prev?.length) return columns
      return columns.map((col) => {
        const existing = prev.find((p) => p.key === col.key)
        return existing ? { ...col, show: existing.show } : col
      })
    })
  }, [columns])

  const columnsMemo = useMemo(() => {
    const visible = newColumns.filter((column) => column.show)
    let stickyLeft = 0
    return visible.map((column) => {
      if (!column.className?.includes('sticky-col')) return column
      const updatedColumn = {
        ...column,
        className: column.className.replace(/left-\d+px/g, ''),
        stickyLeft
      }
      const colWidth = column.width || column.minWidth || 0
      stickyLeft += colWidth
      return updatedColumn
    })
  }, [newColumns])

  const hasIndexColumn = columnsMemo?.some((column) => column.key === 'index')

  const visibleRows = React.useMemo(
    () =>
      !hasIndexColumn
        ? [...rows].sort(getComparator(order, orderBy, newColumns)) || []
        : [...rows].sort(getComparator(order, orderBy, newColumns)).map((row, index) => ({
            ...row,
            index: index + 1 + rowsPerPage * skip
          })) || [],
    [rows, order, orderBy, newColumns, hasIndexColumn, rowsPerPage, skip]
  )

  return (
    <TableContainer className={`absolute inset-0 flex flex-col ${props?.className ?? ''}`} component={Box}>
      {showSetting && (
        <Settings
          actionsSetting={actionsSetting}
          id={id}
          totalRows={totalRows}
          page={page}
          rowsPerPage={rowsPP}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          columns={columns.map((column) => ({
            ...column,
            show: columnsMemo.some((col) => col.key === column.key)
          }))}
          handleShowColumns={handleShowColumns}
        />
      )}
      <Table>
        <TableHeadColumns
          id={id}
          columns={columnsMemo}
          showSetting={showSetting}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody
          showSetting={showSetting}
          id={id}
          rows={visibleRows}
          columns={columnsMemo}
          colSpan={columnsMemo.length}
          loading={loading}
          rowsPerPage={rowsPerPage}
          page={page}
          labelNoRows={labelNoRows}
          groupBy={groupBy}
          orderGroupBy={orderGroupBy}
          groupByRender={groupByRender}
        />
      </Table>
    </TableContainer>
  )
}
export default DataGrid
